import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { authService, AdminUser } from '../../utils/auth';
import { UserInfo } from '../../components/auth/UserInfo';
import { LogoutButton } from '../../components/auth/LogoutButton';

const navigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: '📊' },
  { name: 'Organizations', href: '/admin/organizations', icon: '🏢' },
  { name: 'Job Templates', href: '/admin/job-templates', icon: '📋' },
  { name: 'Interviews', href: '/admin/interviews', icon: '🎤' },
  { name: 'Reports', href: '/admin/reports', icon: '📄' },
  { name: 'User Management', href: '/admin/users', icon: '👥', superAdminOnly: true },
];

export const AdminLayout: React.FC = () => {
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  // Filter navigation items based on user permissions
  const filteredNavigation = navigation.filter(item => {
    if (item.superAdminOnly) {
      return currentUser?.is_super_admin === true;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            {/* Logo Section */}
            <div className="flex items-center space-x-3 min-w-0">
              <div className="text-2xl">🎯</div>
              <div className="hidden sm:block">
                <h1 className="text-sm font-semibold text-gray-900">Snwolley Portal</h1>
                <p className="text-xs text-gray-500 hidden md:block">Admin Dashboard</p>
              </div>
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-gray-900">Snwolley</h1>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden lg:flex items-center space-x-1 mx-4">
              {filteredNavigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <span className="text-sm">{item.icon}</span>
                    <span className="whitespace-nowrap hidden xl:inline">{item.name}</span>
                  </Link>
                );
              })}
            </nav>

            {/* User Section */}
            <div className="flex items-center space-x-3 min-w-0">
              <div className="hidden lg:block">
                <UserInfo showRole={true} showEmail={false} className="text-right" />
              </div>
              <div className="w-px h-6 bg-gray-200 hidden lg:block"></div>
              <LogoutButton 
                showIcon={true} 
                className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:shadow-sm" 
              />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <nav className="lg:hidden bg-white border-b border-gray-200 shadow-sm">
        <div className="px-4 py-2">
          <div className="flex space-x-1 overflow-x-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <span className="text-sm">{item.icon}</span>
                  <span className="sm:inline hidden">{item.name}</span>
                </Link>
              );
            })}
          </div>
          
          {/* Mobile User Info */}
          <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
            <UserInfo showRole={false} showEmail={false} />
            <LogoutButton 
              showIcon={false} 
              className="text-red-600 hover:text-red-700 text-sm px-2 py-1 hover:bg-red-50 rounded-lg transition-colors" 
            />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
};